import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ArrowLeft, Copy, Check, Loader2, ShieldCheck, Clock, QrCode, Wallet, Building2, Store } from 'lucide-react';
import { paymentAPI } from '../services/api';

/**
 * Alur pembayaran versi Stockara.
 * Duitku tetap jadi payment gateway di belakang layar, tapi seluruh tampilan
 * (pilih metode, nomor VA, QRIS, hitung mundur) dirender di sini pakai bahasa
 * visual Stockara: kartu putih, aksen emerald, tanpa logo/branding gateway.
 */

const fmtRp = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

const GROUPS = [
  { id: 'qris', label: 'QRIS', desc: 'Scan pakai apa saja', icon: QrCode },
  { id: 'ewallet', label: 'E-Wallet', desc: 'OVO, DANA, ShopeePay', icon: Wallet },
  { id: 'va', label: 'Transfer Bank', desc: 'Virtual Account', icon: Building2 },
  { id: 'retail', label: 'Gerai Retail', desc: 'Indomaret, Alfamart', icon: Store },
];

const fmtCountdown = (ms) => {
  if (ms <= 0) return '00:00';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (v) => String(v).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

const CopyRow = ({ label, value, mono = true }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(String(value || '')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-900/10 bg-white px-4 py-3">
      <div className="min-w-0">
        <p className="text-[11px] text-slate-400">{label}</p>
        <p className={`text-slate-900 truncate ${mono ? 'font-mono text-base tracking-wide' : 'text-sm font-medium'}`}>{value}</p>
      </div>
      <button
        onClick={copy}
        className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 flex-shrink-0"
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
        {copied ? 'Tersalin' : 'Salin'}
      </button>
    </div>
  );
};

// QR dirender di backend jadi PNG data-URI (lihat _qris_png_data_uri di server.py),
// jadi frontend tidak butuh library QR sama sekali. Kalau backend gagal render,
// tampilkan kode QRIS mentah supaya user tetap bisa menyalinnya.
const QrBlock = ({ image, value }) => {
  if (image) {
    return (
      <div className="flex justify-center rounded-xl border border-emerald-900/10 bg-white p-4">
        <img src={image} alt="Kode QRIS pembayaran" className="h-56 w-56" />
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
      <p className="mb-2 text-xs text-amber-800">
        Kode QR tidak bisa ditampilkan. Salin kode di bawah, lalu tempel di aplikasi pembayaran kamu.
      </p>
      <p className="break-all font-mono text-[10px] text-slate-600">{value}</p>
    </div>
  );
};

const PaymentSheet = ({ kind, tier, packageId, title, subtitle, onClose, onPaid }) => {
  const [step, setStep] = useState('methods');   // methods | instruction | success | expired
  const [amount, setAmount] = useState(0);
  const [methods, setMethods] = useState([]);
  const [activeGroup, setActiveGroup] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState('');
  const [error, setError] = useState('');
  const [tx, setTx] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [phone, setPhone] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    paymentAPI
      .getMethods({ kind, tier, package: packageId })
      .then((res) => {
        if (!alive) return;
        setAmount(res.data.amount || 0);
        const list = res.data.methods || [];
        setMethods(list);
        const firstGroup = GROUPS.find((g) => list.some((m) => m.group === g.id));
        setActiveGroup(firstGroup ? firstGroup.id : '');
      })
      .catch((e) => alive && setError(e?.response?.data?.detail || 'Gagal memuat metode pembayaran'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [kind, tier, packageId]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // Hitung mundur batas waktu bayar
  useEffect(() => {
    if (step !== 'instruction' || !tx?.expires_at) return undefined;
    const end = new Date(tx.expires_at.endsWith('Z') ? tx.expires_at : tx.expires_at + 'Z').getTime();
    const tick = () => {
      const left = end - Date.now();
      setRemaining(left);
      if (left <= 0) { stopPolling(); setStep('expired'); }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [step, tx, stopPolling]);

  const startPolling = useCallback((orderId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await paymentAPI.getStatus(orderId);
        if (res.data?.status === 'paid') {
          stopPolling();
          setStep('success');
          if (onPaid) onPaid(res.data);
        } else if (res.data?.expired) {
          stopPolling();
          setStep('expired');
        }
      } catch (e) { /* diamkan, coba lagi di tick berikutnya */ }
    }, 4000);
  }, [stopPolling, onPaid]);

  const choose = async (method) => {
    setCreating(method.code);
    setError('');
    try {
      const res = await paymentAPI.create({ kind, tier, package: packageId, method: method.code, phone });
      setTx({ ...res.data, methodName: method.name, group: method.group });
      setStep('instruction');
      startPolling(res.data.order_id);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Gagal membuat pembayaran');
    } finally {
      setCreating('');
    }
  };

  const visibleGroups = GROUPS.filter((g) => methods.some((m) => m.group === g.id));
  const groupMethods = methods.filter((m) => m.group === activeGroup);

  const Header = () => (
    <div className="flex items-start justify-between gap-3 border-b border-emerald-900/10 px-5 py-4">
      <div className="flex items-start gap-3 min-w-0">
        {step === 'instruction' && (
          <button
            onClick={() => { stopPolling(); setStep('methods'); setTx(null); }}
            className="mt-0.5 rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <ArrowLeft size={17} />
          </button>
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">
            {step === 'success' ? 'Pembayaran berhasil' : step === 'expired' ? 'Waktu pembayaran habis' : title}
          </p>
          {step === 'methods' && subtitle && <p className="mt-0.5 text-xs text-slate-400">{subtitle}</p>}
          {step === 'instruction' && <p className="mt-0.5 text-xs text-slate-400">{tx?.methodName}</p>}
        </div>
      </div>
      <button onClick={() => { stopPolling(); onClose(); }} className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 flex-shrink-0">
        <X size={17} />
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
      <div className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-t-2xl border border-emerald-900/10 bg-[#FCFAF6] shadow-2xl sm:rounded-2xl">
        <Header />

        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-3.5 py-2.5 text-xs text-red-600">{error}</div>
          )}

          {/* ── Ringkasan nominal ── */}
          {step !== 'success' && step !== 'expired' && (
            <div className="mb-4 rounded-xl border border-emerald-900/10 bg-white px-4 py-3">
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-slate-400">Total bayar</span>
                <span className="text-xl font-bold text-slate-900">
                  {fmtRp(step === 'instruction' && tx ? tx.total_amount : amount)}
                </span>
              </div>
              {step === 'instruction' && tx && tx.total_amount > amount && (
                <p className="mt-1 text-[11px] text-slate-400">
                  Termasuk biaya layanan {fmtRp(tx.total_amount - amount)}. Transfer tepat sampai digit terakhir ya.
                </p>
              )}
            </div>
          )}

          {/* ── Langkah 1: pilih metode ── */}
          {step === 'methods' && (
            loading ? (
              <div className="space-y-2">
                {[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-slate-100" />)}
              </div>
            ) : (
              <>
                <div className="mb-4 rounded-xl border border-emerald-900/10 bg-white px-4 py-3">
                  <label className="block text-[11px] text-slate-400">Nomor WhatsApp (opsional)</label>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ''))}
                    placeholder="08xxxxxxxxxx"
                    inputMode="tel"
                    className="mt-1 w-full border-0 p-0 text-sm text-slate-900 placeholder-slate-300 focus:outline-none focus:ring-0"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">
                    Kami kirimkan pengingat kalau pembayaranmu belum selesai sebelum kodenya kedaluwarsa.
                  </p>
                </div>

                <div className="mb-3 flex gap-1.5 overflow-x-auto pb-1">
                  {visibleGroups.map((g) => {
                    const Icon = g.icon;
                    const on = activeGroup === g.id;
                    return (
                      <button
                        key={g.id}
                        onClick={() => setActiveGroup(g.id)}
                        className={`flex flex-shrink-0 items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-all ${
                          on ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-emerald-900/10 bg-white text-slate-500 hover:border-emerald-200'
                        }`}
                      >
                        <Icon size={13} />
                        {g.label}
                      </button>
                    );
                  })}
                </div>

                <div className="space-y-2">
                  {groupMethods.map((m) => (
                    <button
                      key={m.code}
                      onClick={() => choose(m)}
                      disabled={!!creating}
                      className="flex w-full items-center gap-3 rounded-xl border border-emerald-900/10 bg-white p-3.5 text-left transition-all hover:border-emerald-300 disabled:opacity-50"
                    >
                      {m.image ? (
                        <img src={m.image} alt="" className="h-6 w-10 flex-shrink-0 object-contain" />
                      ) : (
                        <div className="h-6 w-10 flex-shrink-0 rounded bg-slate-100" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-900">{m.name}</p>
                        {m.fee > 0 && <p className="text-[11px] text-slate-400">Biaya layanan {fmtRp(m.fee)}</p>}
                      </div>
                      {creating === m.code && <Loader2 size={15} className="flex-shrink-0 animate-spin text-emerald-600" />}
                    </button>
                  ))}
                  {!groupMethods.length && (
                    <p className="py-6 text-center text-sm text-slate-400">Belum ada metode di kategori ini.</p>
                  )}
                </div>
              </>
            )
          )}

          {/* ── Langkah 2: instruksi bayar ── */}
          {step === 'instruction' && tx && (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5">
                <Clock size={14} className="text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">Selesaikan dalam {fmtCountdown(remaining)}</span>
              </div>

              {tx.qr_string ? (
                <>
                  <QrBlock image={tx.qr_image} value={tx.qr_string} />
                  <p className="text-center text-xs text-slate-500">
                    Buka aplikasi bank atau e-wallet kamu, pilih bayar QRIS, lalu scan kode di atas.
                  </p>
                </>
              ) : tx.va_number ? (
                <>
                  <CopyRow label="Nomor Virtual Account" value={tx.va_number} />
                  <CopyRow label="Jumlah transfer" value={fmtRp(tx.total_amount)} mono={false} />
                  <div className="rounded-xl border border-emerald-900/10 bg-white px-4 py-3">
                    <p className="mb-1.5 text-[11px] font-semibold text-slate-500">Cara bayar</p>
                    <ol className="list-decimal space-y-1 pl-4 text-xs text-slate-500">
                      <li>Buka aplikasi mobile banking atau ATM.</li>
                      <li>Pilih menu Transfer, lalu Virtual Account.</li>
                      <li>Masukkan nomor di atas dan transfer sesuai jumlahnya.</li>
                      <li>Paket kamu aktif otomatis setelah pembayaran masuk.</li>
                    </ol>
                  </div>
                </>
              ) : tx.payment_url ? (
                <>
                  <a
                    href={tx.payment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full rounded-xl bg-emerald-600 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
                  >
                    Lanjutkan Pembayaran
                  </a>
                  <p className="text-center text-xs text-slate-500">
                    Kamu akan diarahkan ke aplikasi pembayaran. Setelah selesai, kembali ke halaman ini.
                  </p>
                </>
              ) : null}

              <div className="flex items-center justify-center gap-2 pt-1 text-xs text-slate-400">
                <Loader2 size={12} className="animate-spin" />
                Menunggu pembayaran, halaman ini akan berubah sendiri
              </div>
            </div>
          )}

          {/* ── Selesai ── */}
          {step === 'success' && (
            <div className="py-6 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
                <Check size={26} className="text-emerald-600" />
              </div>
              <p className="mb-1 text-base font-semibold text-slate-900">Pembayaran diterima</p>
              <p className="mb-6 text-sm text-slate-500">
                {kind === 'topup' ? 'Kredit AI kamu sudah ditambahkan.' : 'Paket kamu sudah aktif. Selamat menikmati fitur barunya.'}
              </p>
              <button
                onClick={() => { if (onPaid) onPaid({ status: 'paid' }); onClose(); }}
                className="w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
              >
                Selesai
              </button>
            </div>
          )}

          {/* ── Kedaluwarsa ── */}
          {step === 'expired' && (
            <div className="py-6 text-center">
              <p className="mb-1 text-base font-semibold text-slate-900">Waktu pembayaran habis</p>
              <p className="mb-6 text-sm text-slate-500">Tidak ada dana yang terpotong. Kamu bisa mengulang pembayaran kapan saja.</p>
              <button
                onClick={() => { setStep('methods'); setTx(null); setError(''); }}
                className="w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
              >
                Ulangi Pembayaran
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-1.5 border-t border-emerald-900/10 px-5 py-3">
          <ShieldCheck size={13} className="text-emerald-600" />
          <span className="text-[11px] text-slate-400">Pembayaran diproses dengan enkripsi standar perbankan</span>
        </div>
      </div>
    </div>
  );
};

export default PaymentSheet;
